// API client configuration for TanStack Query
// Includes JWT token handling for authenticated requests

import { AuthLoginData, AuthLoginResult, AuthToken } from "../types/api";
import { QueryFunctionContext } from "@tanstack/react-query";

// API base URL - uses the proxy rewrite to avoid CORS issues
const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

// JWT token storage key
const TOKEN_KEY = "jwt-token";

/**
 * Get the stored JWT access token
 */
export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  const authToken = localStorage.getItem(TOKEN_KEY) as string | null;
  const parsedAuthToken = authToken ? JSON.parse(authToken) as AuthToken : null;
  return parsedAuthToken ? parsedAuthToken.access_token : null;
}

/**
 * Get the stored JWT refresh token
 */
export function getRefreshToken(): string | null {
  if (typeof window === "undefined") return null;
  const refreshToken = localStorage.getItem(TOKEN_KEY) as string | null;
  const parsedRefreshToken = refreshToken ? JSON.parse(refreshToken) as AuthToken : null;
  return parsedRefreshToken ? parsedRefreshToken.refresh_token : null;
}

/**
 * Store the JWT token
 */
export function setToken(token: AuthToken): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
  // Also set as cookie for proxy to access
  document.cookie = `${TOKEN_KEY}=${JSON.stringify(token)}; path=/; max-age=${60 * 15}`; // 15 minutes
}

/**
 * Remove the JWT token (logout)
 */
export function removeToken(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(TOKEN_KEY);
  document.cookie = `${TOKEN_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getToken();
}

/**
 * Default headers for API requests
 */
function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const token = getToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
}

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
// Queue of requests waiting for token refresh
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeToRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(newToken: string) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

/**
 * Generic fetch wrapper with error handling and token refresh
 */
async function fetchWithAuth<T>(
  url: string,
  options: RequestInit = {},
  retry = true
): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getHeaders(),
      ...options.headers,
    },
  });

  if (!response.ok) {
    // Handle specific error statuses
    if (response.status === 401 && retry) {
      // Unauthorized - token might be expired, try to refresh
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshToken();
          setToken(newToken);
          onTokenRefreshed(newToken.access_token);
          isRefreshing = false;
          // Retry the original request
          return fetchWithAuth<T>(url, options, false);
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = [];
          // Refresh failed, remove token and redirect
          removeToken();
          if (typeof window !== "undefined") {
            window.location.href = "/login";
          }
          throw refreshError;
        }
      } else {
        // Wait for refresh to complete and retry
        return new Promise((resolve, reject) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          subscribeToRefresh((newToken) => {
            // Retry with new token
            fetchWithAuth<T>(url, options, false)
              .then(resolve)
              .catch(reject);
          });
        });
      }
    }

    const errorBody = await response.json().catch(() => ({
      status: { error: { message: `HTTP error! status: ${response.status}`, errors: [] } }
    }));
    throw errorBody;
  }

  return response.json();
}


/**
 * Refetch the access_token with refresh_token
 * Uses plain fetch to avoid recursion with fetchWithAuth
 */
export async function refreshToken(): Promise<AuthToken> {
  const token = getRefreshToken();
  const rawStoredToken =
    typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  let storedAuthToken: Partial<AuthToken> | null = null;
  if (rawStoredToken) {
    try {
      storedAuthToken = JSON.parse(rawStoredToken) as Partial<AuthToken>;
    } catch {
      storedAuthToken = null;
    }
  }
  const refreshPayload = {
    data: {
      refresh_token: token
    }
  };
  const response = await fetch(`${API_BASE_URL}/api/v1/ultimate/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(refreshPayload),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({
      status: { error: { message: `HTTP error! status: ${response.status}`, errors: [] } }
    }));
    throw errorBody;
  }

  const refreshedToken = (await response.json()) as {
    data?: Partial<AuthToken>;
  } & Partial<AuthToken>;
  const refreshedData = (refreshedToken?.data ?? refreshedToken) as Partial<AuthToken>;
  const normalizedToken: AuthToken = {
    access_token: refreshedData.access_token || "",
    access_expires_in: Number(
      refreshedData.access_expires_in ?? storedAuthToken?.access_expires_in ?? 0
    ),
    refresh_token: refreshedData.refresh_token || token || storedAuthToken?.refresh_token || "",
    refresh_expires_in: Number(
      refreshedData.refresh_expires_in ?? storedAuthToken?.refresh_expires_in ?? 0
    ),
  };
  if (!normalizedToken.access_token || !normalizedToken.refresh_token) {
    throw new Error("Invalid refresh token response payload");
  }

  return normalizedToken;
}

/**
 * TanStack Query compatible query function
 * Usage:
 * const { data } = useQuery({
 *   queryKey: ['users', userId],
 *   queryFn: fetchQuery
 * })
 */
export async function fetchQuery<T>(
  context: QueryFunctionContext
): Promise<T> {
  const { queryKey } = context;
  const [endpoint, params] = queryKey as unknown as [string, Record<string, unknown>?];
  
  let url = `${API_BASE_URL}${endpoint}`;
  
  // Append query params if provided
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  return fetchWithAuth<T>(url);
}

/**
 * TanStack Query compatible mutation function factory
 * Usage:
 * const mutation = useMutation({
 *   mutationFn: createMutation('/users')
 * })
 */
export function createMutation<TData = unknown, TResponse = TData>(
  endpoint: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE" = "POST"
) {
  return async (data: TData): Promise<TResponse> => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    if (method === "DELETE") {
      return fetchWithAuth<TResponse>(url, {
        method,
      });
    }

    return fetchWithAuth<TResponse>(url, {
      method,
      body: JSON.stringify(data),
    });
  };
}

/**
 * Pre-configured API methods for common operations
 */
export const api = {
  get: async <T>(endpoint: string, params?: Record<string, unknown>): Promise<T> => {
    let url = `${API_BASE_URL}${endpoint}`;
    
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithAuth<T>(url);
  },

  post: <TData, TResponse = TData>(endpoint: string) =>
    createMutation<TData, TResponse>(endpoint, "POST"),

  put: <TData, TResponse = TData>(endpoint: string) =>
    createMutation<TData, TResponse>(endpoint, "PUT"),

  patch: <TData, TResponse = TData>(endpoint: string) =>
    createMutation<TData, TResponse>(endpoint, "PATCH"),

  delete: <TResponse = void>(endpoint: string) =>
    createMutation<unknown, TResponse>(endpoint, "DELETE"),
};

/**
 * Example: Login function
 * Stores the JWT token after successful login
 * Uses plain fetch to avoid automatic token refresh on 401 errors
 */
export async function login(data: AuthLoginData): Promise<AuthLoginResult> {
  const response = await fetch(`${API_BASE_URL}/api/v1/ultimate/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      data: data
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({
      status: { error: { message: `HTTP error! status: ${response.status}`, errors: [] } }
    }));
    throw errorBody;
  }

  const result = await response.json();
  setToken(result.data);
  return result;
}

/**
 * Example: Logout function
 * Clears the JWT token
 */
export function logout(): void {
  removeToken();
  // Optionally call the backend logout endpoint
  // fetchWithAuth(`${API_BASE_URL}/auth/logout`, { method: "POST" });
}

// Expose refreshToken globally for external JS access
if (typeof window !== "undefined") {
  (window as any).steelytoe_refreshToken = refreshToken;
  (window as any).steelytoe_getToken = getToken;
  (window as any).steelytoe_getRefreshToken = getRefreshToken;
  (window as any).steelytoe_setToken = setToken;
  (window as any).steelytoe_removeToken = removeToken;
  (window as any).steelytoe_isAuthenticated = isAuthenticated;
}
